import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { APP_ENVIRONMENT } from "../config";
import { fonts } from "../theme/tokens";
import Eyebrow from "../components/brand/Eyebrow";
import ScreenshotAnnotator from "./ScreenshotAnnotator";
import { feedbackDialogSlotProps } from "./overlayZIndex";

export default function FeedbackCollectorDialog({
  open,
  onClose,
  excludeAttr,
  title = "Feedback",
  categoryOptions = [],
  categoryLabel = "Category",
  commentLabel = "Comment",
  submitLabel = "Send feedback",
  requireComment = true,
  userLine = "Signed-in user",
  capturedAt = "",
  screenshot = null,
  captureFailed = false,
  allowAttachments = false,
  attachmentAccept = ".har,application/json,.json",
  attachmentHint = "",
  onSubmit,
}) {
  const [category, setCategory] = useState(categoryOptions[0]?.value ?? "");
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const annotatorRef = useRef(null);

  const resetForm = () => {
    setCategory(categoryOptions[0]?.value ?? "");
    setComment("");
    setAttachment(null);
    setFormError("");
  };

  const closeDialog = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = comment.trim();
    if (requireComment && !trimmed) {
      setFormError("Add a comment so we know what to look at.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      const image = annotatorRef.current
        ? await annotatorRef.current.exportJpeg()
        : screenshot;
      await onSubmit({
        category,
        comment: trimmed,
        image,
        attachment,
        pageUrl: window.location.href,
        pagePath: window.location.pathname,
      });
      resetForm();
      onClose();
    } catch (err) {
      setFormError(err?.message || "Unable to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      slotProps={feedbackDialogSlotProps()}
    >
      <Box component="form" onSubmit={handleSubmit} {...{ [excludeAttr]: true }}>
        <DialogTitle sx={{ fontFamily: fonts.serif }}>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            {captureFailed && (
              <Alert severity="warning">
                Screenshot unavailable. You can still send the report.
              </Alert>
            )}
            {categoryOptions.length > 0 && (
              <TextField
                select
                label={categoryLabel}
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                fullWidth
                required
              >
                {categoryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={commentLabel}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              fullWidth
              required={requireComment}
              multiline
              minRows={3}
            />
            {screenshot && <ScreenshotAnnotator ref={annotatorRef} blob={screenshot} />}
            {allowAttachments && (
              <Stack spacing={0.5}>
                <Button variant="outlined" component="label">
                  + Attach file (e.g. HAR)
                  <input
                    type="file"
                    hidden
                    accept={attachmentAccept}
                    onChange={(event) => setAttachment(event.target.files?.[0] || null)}
                  />
                </Button>
                {attachment && (
                  <Typography variant="body2" color="text.secondary">
                    {attachment.name} ({Math.round(attachment.size / 1024)} KB)
                  </Typography>
                )}
                {attachmentHint && (
                  <Typography variant="caption" color="text.secondary">
                    {attachmentHint}
                  </Typography>
                )}
              </Stack>
            )}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                px: 2,
                py: 1.5,
              }}
            >
              <Eyebrow>Will be sent</Eyebrow>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {document.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {window.location.href}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userLine}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(capturedAt || "—") + (APP_ENVIRONMENT ? ` · ${APP_ENVIRONMENT}` : "")}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={closeDialog}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={submitting || (requireComment && !comment.trim())}
          >
            {submitting ? "Sending…" : submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
